import { useEffect, useState } from "react";
import tw from "tailwind-styled-components";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
    InputGroup,
    Label,
    ActionSection,
    SideCancelBtn,
    SubmitBtn,
    ModalHeader,
    Wrapper,
    ScrollableContent,
} from "../Styles/InputStyles";
import CategoryCard from "../CategoryCard";
import SelectItemModal from "../SelectItemModal";
import MultiPropSelectModal from "../MultiPropSelectModal";
import SelectOptionModal from "../SelectOptionModal";
import TimeBoxesInput from "../Common/TimeBoxesInput";
import MultiEmailInput from "../Common/MultiEmailInput";
import { MdClose } from "react-icons/md";
import moment from "moment";

const isValidTime = (val) => {
    if (!val) return false;
    const str = String(val).trim();

    const m12 = str.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
    if (m12) {
        const h = parseInt(m12[1], 10);
        const min = parseInt(m12[2], 10);
        return h >= 1 && h <= 12 && min >= 0 && min <= 59;
    }

    const m24 = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m24) {
        const h = parseInt(m24[1], 10);
        const min = parseInt(m24[2], 10);
        return h >= 0 && h <= 23 && min >= 0 && min <= 59;
    }

    return false;
};

export const scheduleSchema = Yup.object().shape({
    reportType: Yup.number()
        .required("Report type is required")
        .min(1, "Report type is required"),
    frequency: Yup.number()
        .required("Frequency is required")
        .oneOf([1, 2, 3], "Invalid frequency"),
    time: Yup.string()
        .required("Time is required")
        .test(
            "valid-time",
            "Enter a valid time (e.g. 10:00 AM)",
            (val) => isValidTime(val)
        ),
    emails: Yup.array()
        .of(
            Yup.string()
                .trim()
                .email("Enter a valid email")
                .required("Email is required")
        )
        .min(1, "Add at least one email")
        .required("Add at least one email"),
    properties: Yup.array()
        .min(1, "Select at least one property")
        .required("Select at least one property"),
});

export const INITIAL_VALUES = {
    reportType: null,
    reportTypeName: "",
    frequency: "",
    time: "9:00 AM",
    emails: [],
    properties: [],
};

const AddEditScheduledReport = ({
    reportTypes = [],
    frequencyOptions = [],
    propertyOptions = [],
    initialValues = INITIAL_VALUES,
    onSubmit,
    onCancel,
    isEditing = false,
    isSubmitting = false,
    formikRef,
    submitLabel,
    handleClose,
}) => {
    const [height, setHeight] = useState(window.innerHeight);
    const [showReportTypeModal, setShowReportTypeModal] = useState(false);
    const [showFrequencyModal, setShowFrequencyModal] = useState(false);
    const [showPropertyModal, setShowPropertyModal] = useState(false);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
        const handleResize = () => {
            setHeight(window.innerHeight);
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const getReportTitle = (reportType) =>
        reportTypes.find((r) => r.reportType === reportType)?.title ?? "-";

    const formatTime = (val) => {
        if (!val) return "";
        const parsed = moment(val, ["h:mm A", "HH:mm:ss", "HH:mm", "h:mm"], true);
        return parsed.isValid() ? parsed.format("h:mm A") : val;
    };
    return (
        <Container style={{ height: `${height}px`, overflow: "auto" }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">
                    {isEditing ? "Edit Schedule Report" : "Add Schedule Report"}
                </h1>
                <button onClick={handleClose} type="button">
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>
            <div className="px-6 h-full flex flex-col">
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={scheduleSchema}
                    onSubmit={onSubmit}
                    validateOnChange
                    validateOnBlur
                    enableReinitialize
                >
                    {({ values, errors, touched, setFieldValue, submitForm }) => {
                        const reportTypeCategory =
                            values.reportType &&
                                getReportTitle(values.reportType) !== "-"
                                ? { label: getReportTitle(values.reportType) }
                                : null;

                        const frequencyLabel = frequencyOptions.find(
                            (f) => String(f.value) === String(values.frequency)
                        )?.label;
                        const frequencyCategory = frequencyLabel
                            ? { label: frequencyLabel }
                            : null;

                        const propertyCategory =
                            values.properties && values.properties.length > 0
                                ? { label: `${values.properties.length} selected` }
                                : null;

                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100vh-140px)]">
                                    <Wrapper>
                                        {/* <InputGroup>
                                            <Label>Report Type *</Label>
                                            <CategoryCard
                                                title="Report Type"
                                                selectedCategory={reportTypeCategory}
                                                onClick={() => {
                                                    if (!isEditing) {
                                                        setShowReportTypeModal(true);
                                                    }
                                                }}
                                            />
                                            {errors.reportType && touched.reportType && (
                                                <ErrorText>{errors.reportType}</ErrorText>
                                            )}
                                        </InputGroup> */}
                                        <InputGroup>
                                            <Label>Report Type *</Label>

                                            <div
                                                className={`rounded-lg ${isEditing ? "cursor-not-allowed opacity-80" : ""
                                                    }`}
                                            >
                                                <CategoryCard
                                                    title="Report Type"
                                                    selectedCategory={reportTypeCategory}

                                                    onClick={() => {
                                                        if (isEditing) return;
                                                        setShowReportTypeModal(true);
                                                    }}
                                                    disabled={isEditing}
                                                />
                                            </div>

                                            {isEditing && (
                                                <p className="mt-1 text-xs text-gray-400">
                                                    Report type cannot be changed after creation.
                                                </p>
                                            )}

                                            {errors.reportType && touched.reportType && (
                                                <ErrorText>{errors.reportType}</ErrorText>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label>Frequency *</Label>
                                            <CategoryCard
                                                title="Frequency"
                                                selectedCategory={frequencyCategory}
                                                onClick={() => setShowFrequencyModal(true)}
                                            />
                                            {errors.frequency && touched.frequency && (
                                                <ErrorText>{errors.frequency}</ErrorText>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label>Time *</Label>
                                            <TimeBoxesInput
                                                value={values.time}
                                                onChange={(val) => {
                                                    setFieldValue("time", val);
                                                    // Manually trigger validation if needed, or rely on validateOnChange
                                                }}
                                            />
                                            <p className="mt-2 text-sm text-gray-500">
                                                {String(frequencyLabel || "").toLowerCase().includes("weekly")
                                                    ? `Report will be sent every Sunday at ${formatTime(values.time)}.`
                                                    : String(frequencyLabel || "").toLowerCase().includes("monthly")
                                                        ? `Report will be sent on the last day of every month at ${formatTime(values.time)}.`
                                                        : String(frequencyLabel || "").toLowerCase().includes("daily")
                                                            ? `Report will be sent every day at ${formatTime(values.time)}.`
                                                            : ""}
                                            </p>
                                            {errors.time && touched.time && (
                                                <ErrorText>{errors.time}</ErrorText>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label>Email(s) *</Label>
                                            <MultiEmailInput
                                                value={values.emails}
                                                onChange={(list) =>
                                                    setFieldValue("emails", list)
                                                }
                                                placeholder="Enter email and press Enter"
                                            />
                                            {errors.emails && touched.emails && (
                                                <ErrorText>
                                                    {Array.isArray(errors.emails)
                                                        ? errors.emails.find(Boolean) ||
                                                        "Invalid email"
                                                        : errors.emails}
                                                </ErrorText>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label>Select Property *</Label>
                                            <CategoryCard
                                                title="Property"
                                                selectedCategory={propertyCategory}
                                                onClick={() => setShowPropertyModal(true)}
                                            />
                                            {errors.properties && touched.properties && (
                                                <ErrorText>{errors.properties}</ErrorText>
                                            )}
                                        </InputGroup>
                                    </Wrapper>
                                </ScrollableContent>
                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={onCancel}>
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn
                                        type="button"
                                        onClick={submitForm}
                                        disabled={isSubmitting}
                                        className="disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting
                                            ? isEditing
                                                ? "Updating..."
                                                : "Submitting..."
                                            : submitLabel || (isEditing ? "Update" : "Submit")}
                                    </SubmitBtn>
                                </ActionSection>
                                {/* Modals remain unchanged... */}
                                <SelectItemModal
                                    isOpen={showReportTypeModal}
                                    onClose={() => setShowReportTypeModal(false)}
                                    onSelect={(option) => {
                                        if (option) {
                                            setFieldValue(
                                                "reportType",
                                                option.reportType ?? option.id
                                            );
                                            setFieldValue("reportTypeName", option.name ?? option.title ?? "");
                                        }
                                    }}
                                    options={reportTypes.map((r) => ({
                                        id: r.reportType,
                                        name: r.title,
                                    }))}
                                    selectedItem={
                                        values.reportType
                                            ? {
                                                id: values.reportType,
                                                name: getReportTitle(
                                                    values.reportType
                                                ),
                                            }
                                            : null
                                    }
                                    title="Select Report Type"
                                    searchPlaceholder="Search report type..."
                                    idKey="id"
                                    nameKey="name"
                                    showClearSelection={false}
                                />

                                <SelectOptionModal
                                    isOpen={showFrequencyModal}
                                    onClose={() => setShowFrequencyModal(false)}
                                    onSelect={(val) => {
                                        if (val) setFieldValue("frequency", val);
                                    }}
                                    options={frequencyOptions}
                                    selectedValue={values.frequency}
                                    title="Select Frequency"
                                    searchPlaceholder="Search frequency..."
                                />

                                <MultiPropSelectModal
                                    isOpen={showPropertyModal}
                                    onClose={() => setShowPropertyModal(false)}
                                    onSelect={(ids) =>
                                        setFieldValue(
                                            "properties",
                                            Array.isArray(ids) ? ids : []
                                        )
                                    }
                                    options={propertyOptions}
                                    selectedProperties={values.properties || []}
                                />
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const FormSection = tw.div`space-y-2`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5 mt-1`;

export default AddEditScheduledReport;